import { gql, useQuery } from '@apollo/client';
import { ModalBody, Stack, Text } from '@chakra-ui/react';
import * as React from 'react';
import {
  AllSearchResults,
  connectAutoComplete,
} from 'react-instantsearch-core';

import {
  IAutoCompleteSearch,
  IEventSearch,
  IFileSearch,
  IMemberSearch,
  IOrganizationSearch,
  IPostSearch,
} from '@/client/legacy-shared-modules/search/search-types';
import { useGlobalSearchContext } from '@/imports/ui/components/search/SearchProvider';

import { GlobalSearchModalBody_Tenant } from './graphql-types/GlobalSearchModalBody_Tenant';

import { Event } from './components/Event';
import { File } from './components/File';
import { Organization } from './components/Organization';
import { Post } from './components/Post';
import { User } from './components/User';
import { sortGlobalSearchSectionsByRelevance } from '@/components/GlobalSearch/utils/sortGlobalSearchSections';

const TENANT = gql`
  query GlobalSearchModalBody_Tenant {
    tenant {
      result {
        _id
        organizationEntityName
      }
    }
  }
`;

export const GlobalSearchModalResults = connectAutoComplete<
  AllSearchResults<IAutoCompleteSearch>
>((props) => {
  const query = useQuery<GlobalSearchModalBody_Tenant>(TENANT);
  const { indexData, indexFilterData } = useGlobalSearchContext();

  if (!indexData) {
    return null;
  }

  if (!props.hits.some(({ hits }) => hits.length)) {
    return null;
  }

  const orgFilter = indexFilterData?.orgIndexFilter ?? '';
  const orgLabel = /(^|\s)isSponsor\s*:\s*true(\s|$)/.test(orgFilter)
    ? 'Sponsors'
    : 'Organizations';

  // Fallback only for equal relevance (old fixed UX order).
  const FALLBACK_PRIORITY: Record<string, number> = {
    [indexData.memberIndexName]: 1,
    [indexData.orgIndexName]: 2,
    [indexData.eventIndexName]: 3,
    [indexData.fileIndexName]: 4,
    [indexData.postIndexName]: 5,
  };

  const LABEL: Record<string, string> = {
    [indexData.memberIndexName]: 'Members',
    [indexData.orgIndexName]: orgLabel,
    [indexData.eventIndexName]: 'Events',
    [indexData.fileIndexName]: 'Files',
    [indexData.postIndexName]: 'Posts',
  };

  const orderedSections = sortGlobalSearchSectionsByRelevance(
    props.hits,
    FALLBACK_PRIORITY
  );

  return (
    <ModalBody borderTop="1px" borderBottom="1px" borderColor="gray.200">
      <Stack>
        {orderedSections.map(({ index, hits }) => {
            if (!hits.length) {
              return null;
            }

            return (
              <Stack key={index}>
                <Text
                  textStyle="detailStrong"
                  textTransform="uppercase"
                  color="gray.500"
                >
                  {LABEL[index] ?? index}
                </Text>

                {hits.map((hit, position) => {
                  const resultPosition = position + 1;
                  const query = props.currentRefinement;

                  if (index === indexData.memberIndexName) {
                    return (
                      <User
                        key={hit.objectID}
                        hit={hit as IMemberSearch}
                        memberIndexViewableField={
                          indexData.memberIndexViewableField
                        }
                        memberIndexSearchableField={
                          indexData.memberIndexSearchableField
                        }
                        query={query}
                        position={resultPosition}
                      />
                    );
                  }

                  if (index === indexData.fileIndexName) {
                    return (
                      <File
                        key={hit.objectID}
                        hit={hit as IFileSearch}
                        query={query}
                        position={resultPosition}
                      />
                    );
                  }

                  if (index === indexData.postIndexName) {
                    return (
                      <Post
                        key={hit.objectID}
                        hit={hit as IPostSearch}
                        query={query}
                        position={resultPosition}
                      />
                    );
                  }

                  if (index === indexData.eventIndexName) {
                    return (
                      <Event
                        key={hit.objectID}
                        hit={hit as IEventSearch}
                        query={query}
                        position={resultPosition}
                      />
                    );
                  }

                  if (index === indexData.orgIndexName) {
                    return (
                      <Organization
                        key={hit.objectID}
                        hit={hit as IOrganizationSearch}
                        query={query}
                        position={resultPosition}
                      />
                    );
                  }

                  return null;
                })}
              </Stack>
            );
          })}
      </Stack>
    </ModalBody>
  );
});
